import { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Building2,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import api from '../../services/api';

// Signal type badge colors
const getCategoryColor = (category) => {
  switch (category) {
    case 'bullish':
      return 'bg-green-100 text-green-700';
    case 'bearish':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'bullish':
      return <TrendingUp className="w-3 h-3" />;
    case 'bearish':
      return <TrendingDown className="w-3 h-3" />;
    default:
      return <Minus className="w-3 h-3" />;
  }
};

// Signal Form Modal
function SignalFormModal({ isOpen, onClose, onSave, signal, buyers, signalTypes }) {
  const [formData, setFormData] = useState({
    buyer_id: '',
    signal_type: '',
    signal_category: 'neutral',
    signal_title: '',
    signal_description: '',
    signal_strength: 5,
    signal_date: new Date().toISOString().split('T')[0],
    source_name: '',
    source_url: '',
    therapeutic_area: '',
    modality: '',
    is_verified: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (signal) {
      setFormData({
        buyer_id: signal.buyer_id || '',
        signal_type: signal.signal_type || '',
        signal_category: signal.signal_category || 'neutral',
        signal_title: signal.signal_title || '',
        signal_description: signal.signal_description || '',
        signal_strength: signal.signal_strength || 5,
        signal_date: signal.signal_date ? signal.signal_date.split('T')[0] : new Date().toISOString().split('T')[0],
        source_name: signal.source_name || '',
        source_url: signal.source_url || '',
        therapeutic_area: signal.therapeutic_area || '',
        modality: signal.modality || '',
        is_verified: signal.is_verified !== false
      });
    } else {
      setFormData({
        buyer_id: '',
        signal_type: '',
        signal_category: 'neutral',
        signal_title: '',
        signal_description: '',
        signal_strength: 5,
        signal_date: new Date().toISOString().split('T')[0],
        source_name: '',
        source_url: '',
        therapeutic_area: '',
        modality: '',
        is_verified: true
      });
    }
  }, [signal, isOpen]);

  // Auto-set category based on signal type
  useEffect(() => {
    const selectedType = signalTypes.find(t => t.value === formData.signal_type);
    if (selectedType) {
      setFormData(prev => ({ ...prev, signal_category: selectedType.category }));
    }
  }, [formData.signal_type, signalTypes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const therapeuticAreas = [
    'Oncology', 'Immunology', 'Neurology', 'Cardiovascular', 'Metabolic',
    'Rare Disease', 'Infectious Disease', 'Respiratory', 'Ophthalmology',
    'Dermatology', 'Hematology', 'GI/Hepatology', 'MASH/NASH', 'Nephrology'
  ];

  const modalities = [
    'Small Molecule', 'Antibody', 'ADC', 'Bispecific', 'CAR-T', 'Cell Therapy',
    'Gene Therapy', 'mRNA', 'siRNA', 'Peptide', 'Protein', 'Vaccine', 'Radiopharmaceutical'
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {signal ? 'Edit Signal' : 'Add New Signal'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Buyer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer *</label>
            <select
              value={formData.buyer_id}
              onChange={(e) => setFormData(prev => ({ ...prev, buyer_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            >
              <option value="">Select Buyer</option>
              {buyers
                .sort((a, b) => (b.signal_count || 0) - (a.signal_count || 0))
                .map(buyer => (
                  <option key={buyer.id} value={buyer.id}>
                    {buyer.company_name} ({buyer.signal_count || 0} signals) [{buyer.id.slice(0, 8)}]
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Select buyer with most signals if duplicates exist</p>
          </div>

          {/* Signal Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signal Type *</label>
              <select
                value={formData.signal_type}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              >
                <option value="">Select Type</option>
                {signalTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.category})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.signal_category}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="bullish">Bullish</option>
                <option value="bearish">Bearish</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.signal_title}
              onChange={(e) => setFormData(prev => ({ ...prev, signal_title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="e.g., Pfizer acquires Seagen for $43B"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.signal_description}
              onChange={(e) => setFormData(prev => ({ ...prev, signal_description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
              placeholder="Additional details about the signal..."
            />
          </div>

          {/* Date and Strength */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signal Date</label>
              <input
                type="date"
                value={formData.signal_date}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Strength: {formData.signal_strength}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.signal_strength}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_strength: parseInt(e.target.value) }))}
                className="w-full"
              />
            </div>
          </div>

          {/* Therapeutic Area and Modality */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Area</label>
              <select
                value={formData.therapeutic_area}
                onChange={(e) => setFormData(prev => ({ ...prev, therapeutic_area: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Area</option>
                {therapeuticAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
              <select
                value={formData.modality}
                onChange={(e) => setFormData(prev => ({ ...prev, modality: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Modality</option>
                {modalities.map(mod => (
                  <option key={mod} value={mod}>{mod}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Source */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
              <input
                type="text"
                value={formData.source_name}
                onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="e.g., Reuters, Company PR"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source URL</label>
              <input
                type="url"
                value={formData.source_url}
                onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Verified checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_verified"
              checked={formData.is_verified}
              onChange={(e) => setFormData(prev => ({ ...prev, is_verified: e.target.checked }))}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <label htmlFor="is_verified" className="text-sm text-gray-700">
              Mark as verified
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {signal ? 'Update Signal' : 'Create Signal'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminSignals() {
  const [signals, setSignals] = useState([]);
  const [signalTypeStats, setSignalTypeStats] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [signalTypes, setSignalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingSignal, setEditingSignal] = useState(null);

  // Load initial data
  useEffect(() => {
    loadBuyers();
    loadSignalTypes();
  }, []);

  // Load signals when filters change
  useEffect(() => {
    loadSignals();
  }, [search, selectedBuyer, selectedType, selectedCategory, pagination.offset]);

  const loadBuyers = async () => {
    try {
      const response = await api.get('/admin/buyers?limit=500');
      setBuyers(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load buyers:', err);
    }
  };

  const loadSignalTypes = async () => {
    try {
      const response = await api.get('/admin/signals/types');
      setSignalTypes(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load signal types:', err);
    }
  };

  const loadSignals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedBuyer) params.set('buyer_id', selectedBuyer);
      if (selectedType) params.set('signal_type', selectedType);
      if (selectedCategory) params.set('signal_category', selectedCategory);
      params.set('limit', pagination.limit);
      params.set('offset', pagination.offset);

      const response = await api.get(`/admin/signals?${params.toString()}`);
      setSignals(response.data?.data?.signals || []);
      setSignalTypeStats(response.data?.data?.signalTypeStats || []);
      setPagination(prev => ({
        ...prev,
        total: response.data?.pagination?.total || 0
      }));
    } catch (err) {
      console.error('Failed to load signals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSignal = async (data) => {
    if (editingSignal) {
      await api.put(`/admin/signals/${editingSignal.id}`, data);
    } else {
      await api.post('/admin/signals', data);
    }
    loadSignals();
  };

  const handleDeleteSignal = async (signal) => {
    if (!confirm(`Delete signal "${signal.signal_title}"?`)) return;
    try {
      await api.delete(`/admin/signals/${signal.id}`);
      loadSignals();
    } catch (err) {
      alert('Failed to delete signal: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (signal) => {
    setEditingSignal(signal);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingSignal(null);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Signal Management</h1>
          <p className="text-gray-600">Add, edit, and manage buyer signals</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Signal
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {signalTypeStats.slice(0, 6).map(stat => (
          <div key={stat.signal_type} className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-lg font-bold text-gray-900">{stat.count}</div>
            <div className="text-xs text-gray-500 truncate">{stat.signal_type}</div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search signals..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-medium ${
              showFilters ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buyer</label>
              <select
                value={selectedBuyer}
                onChange={(e) => setSelectedBuyer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Buyers</option>
                {buyers.map(buyer => (
                  <option key={buyer.id} value={buyer.id}>{buyer.company_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signal Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Types</option>
                {signalTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Categories</option>
                <option value="bullish">Bullish</option>
                <option value="bearish">Bearish</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Signals Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
          </div>
        ) : signals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No signals found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strength</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {signals.map(signal => (
                  <tr key={signal.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(signal.signal_date)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="text-sm font-medium text-gray-900">{signal.buyer_name || 'Unknown'}</span>
                          <p className="text-xs text-gray-400 font-mono">{signal.buyer_id?.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(signal.signal_category)}`}>
                        {getCategoryIcon(signal.signal_category)}
                        {signal.signal_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-900 truncate">{signal.signal_title}</p>
                        {signal.signal_description && (
                          <p className="text-xs text-gray-500 truncate mt-0.5">{signal.signal_description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {signal.therapeutic_area || '-'}
                      {signal.modality && <span className="text-gray-400"> / {signal.modality}</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              signal.signal_strength >= 7 ? 'bg-green-500' :
                              signal.signal_strength >= 4 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${signal.signal_strength * 10}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{signal.signal_strength}/10</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {signal.source_url ? (
                        <a
                          href={signal.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          {signal.source_name || 'Link'}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        signal.source_name || '-'
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(signal)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSignal(signal)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      <SignalFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaveSignal}
        signal={editingSignal}
        buyers={buyers}
        signalTypes={signalTypes}
      />
    </div>
  );
}
